import React, { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";

import { Switch } from "@/components/atoms";
import { useCookieConsent, ViewMode } from "@/shared/contexts";
import { useNavigation, useViewMode } from "@/shared/hooks";

import { CookieCategory } from "../utils";
import { StyledConsentContainer, StyledCookieButton } from "./styled";

const CookieConsent: FC = () => {
    const { viewMode } = useViewMode();
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [openCookieSettings, setOpenCookieSettings] = useState(false);
    const { categories: consentCategories, date, setConsent } = useCookieConsent();
    const { navToCookiePolicyPage } = useNavigation();

    const [state, setState] = useState({
        checkedStrictlyNecessary: true,
        checkedPerformance: consentCategories.includes("performance"),
        checkedFunctionality: consentCategories.includes("functional"),
    });

    useEffect(() => {
        setState({
            checkedStrictlyNecessary: true,
            checkedPerformance: consentCategories.includes("performance"),
            checkedFunctionality: consentCategories.includes("functional"),
        });
    }, [consentCategories]);

    const isConsented = !!date && consentCategories.length > 0;

    const handleNewConsent = (theCategories: CookieCategory[]) => {
        setConsent(theCategories);
        setIsOpen(false);
        setOpenCookieSettings(false);
    };

    const handleChange = (event) => {
        setState({ ...state, [event.target.name]: event.target.checked });
    };

    const handleAcceptAll = () => {
        const preferences: CookieCategory[] = ["strict", "functional", "performance"];
        handleNewConsent(preferences);
    };

    const handleSavePreferences = () => {
        const preferences: CookieCategory[] = ["strict"];
        if (state.checkedPerformance) preferences.push("performance");
        if (state.checkedFunctionality) preferences.push("functional");
        handleNewConsent(preferences);
    };

    const handleReadMore = () => {
        navToCookiePolicyPage();
        if (isConsented) {
            setIsOpen(false);
        }
        setOpenCookieSettings(false);
    };

    if (viewMode === ViewMode.FRAGMENT) return null;

    if (isConsented && !isOpen) {
        return <StyledCookieButton onClick={() => setIsOpen(true)}>{t("Cookie.Cookies")}</StyledCookieButton>;
    }

    return (
        <StyledConsentContainer>
            <div className="title-container">{t("CookieConsent.Title")}</div>
            <div className="infoContainer">
                <div className="messageContainer">
                    <div>
                        {t("CookieConsent.Message1")}{" "}
                        <span onClick={handleReadMore}>{t("CookieConsent.ReadMore")}</span>
                    </div>
                    {t("CookieConsent.Message2")}
                </div>
                <div className="buttonContainer">
                    <Button
                        fullWidth
                        onClick={handleAcceptAll}
                        data-testid="btn-cookie-accept-all"
                        className="mr10 mb10"
                    >
                        {t("CookieConsent.AcceptAll")}
                    </Button>
                    {openCookieSettings ? (
                        <Button variant="outlined" fullWidth onClick={handleSavePreferences} className="mr10 mb10">
                            {t("CookieConsent.SavePreferences")}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setOpenCookieSettings(true)}
                            className="mr10 mb10"
                        >
                            {t("CookieConsent.Settings.Title")}
                        </Button>
                    )}
                </div>
            </div>
            {openCookieSettings && (
                <div className="readMoreContainer">
                    <ul>
                        <li>{t("CookieConsent.Settings.StrictlyNecessary.Title")}</li>
                        <div className="switcher">
                            <div className="text">{t("CookieConsent.Settings.StrictlyNecessary.Message")}</div>
                            <div className="switch">
                                <Switch
                                    disabled
                                    checked={state.checkedStrictlyNecessary}
                                    onChange={handleChange}
                                    color="primary"
                                    name="checkedStrictlyNecessary"
                                    inputProps={{ "aria-label": "primary checkbox" }}
                                />
                            </div>
                        </div>
                        <li>{t("CookieConsent.Settings.Functionality.Title")}</li>
                        <div className="switcher">
                            <div className="text">{t("CookieConsent.Settings.Functionality.Message")}</div>
                            <div className="switch">
                                <Switch
                                    checked={state.checkedFunctionality}
                                    onChange={handleChange}
                                    color="primary"
                                    name="checkedFunctionality"
                                    inputProps={{ "aria-label": "primary checkbox" }}
                                />
                            </div>
                        </div>
                        <li>{t("CookieConsent.Settings.Performance.Title")}</li>
                        <div className="switcher">
                            <div className="text">{t("CookieConsent.Settings.Performance.Message")}</div>
                            <div className="switch">
                                <Switch
                                    checked={state.checkedPerformance}
                                    onChange={handleChange}
                                    color="primary"
                                    name="checkedPerformance"
                                    inputProps={{ "aria-label": "primary checkbox" }}
                                />
                            </div>
                        </div>
                    </ul>
                </div>
            )}
        </StyledConsentContainer>
    );
};

export default CookieConsent;
